import { useId } from "react";
import { compute } from "../calc";
import { SEGMENT_LABEL_MIN_PCT } from "../constants";
import { help, profilePanel, results as copy, sections, vehicleForm } from "../content";
import { useFmt } from "../fmt";
import type { Profile, Vehicle } from "../types";
import { HelpTip } from "./HelpTip";

export function Results({ vehicle: v, profile: p }: { vehicle: Vehicle; profile: Profile }) {
  const fmt = useFmt();
  const headingId = useId();
  const r = compute(v, p, fmt.unit);
  const share = (part: number) => (r.totalPpm ? (part / r.totalPpm) * 100 : 0);
  const fixedShare = share(r.fixedPpm);
  const homeShare = share(r.homeContribution);
  const awayShare = share(r.awayContribution);
  const total = fmt.parts(r.totalPpm);
  const homePct = Math.round(r.homeSharePct);
  /* A segment narrower than this can't hold its figure legibly, so it goes unlabelled. */
  const label = (pct: number, ppm: number) => (pct > SEGMENT_LABEL_MIN_PCT ? fmt.per(ppm) : "");

  return (
    <section className="panel" aria-labelledby={headingId}>
      <h3 id={headingId}>{sections.summary}</h3>
      <div className="sub summary-sub">
        <span className="ellipsis" title={v.name}>
          {v.name || vehicleForm.fallbackName}
        </span>
        <span className="profile-line">
          <span className="ellipsis" title={p.name}>
            {p.name || profilePanel.fallbackName}
          </span>
          <span className="meta">({fmt.profileMeta(p)})</span>
        </span>
      </div>

      <div className="result-total">
        <div>
          <div className="label">
            {copy.totalPerUnit(fmt.unit)}
            <HelpTip label={help.total.label}>{help.total.body(v.fuel, fmt.unit)}</HelpTip>
          </div>
          <p className="value">
            <small>{total.prefix}</small>
            {total.value}
            <small>{total.suffix}</small>
          </p>
        </div>
        <p className="annual">
          {copy.annualTotal}
          <span className="mono-val">{fmt.money(r.annualTotal)}</span>
        </p>
      </div>

      {/* Decorative: the same split is in the breakdown table below. */}
      <div className="stack-bar" aria-hidden="true">
        {fixedShare > 0 && (
          <div style={{ width: `${fixedShare}%`, background: "var(--fixed)" }}>{label(fixedShare, r.fixedPpm)}</div>
        )}
        {homeShare > 0 && (
          <div style={{ width: `${homeShare}%`, background: "var(--home)" }}>{label(homeShare, r.homeContribution)}</div>
        )}
        {awayShare > 0 && (
          <div style={{ width: `${awayShare}%`, background: "var(--away)" }}>{label(awayShare, r.awayContribution)}</div>
        )}
      </div>

      <ul className="legend">
        <li>
          <span className="sw" style={{ background: "var(--fixed)" }} aria-hidden="true" />
          {copy.legendFixed}
        </li>
        <li>
          <span className="sw" style={{ background: "var(--home)" }} aria-hidden="true" />
          {copy.legendHome(homePct, fmt.unit)}
        </li>
        <li>
          <span className="sw" style={{ background: "var(--away)" }} aria-hidden="true" />
          {copy.legendAway(100 - homePct, fmt.unit)}
        </li>
      </ul>

      <table className="breakdown">
        <caption className="sr-only">{copy.tableCaption}</caption>
        <thead>
          <tr>
            <th scope="col">{copy.columnComponent}</th>
            <th scope="col">{copy.columnPerUnit(fmt.unit)}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{copy.rowFixed}</th>
            <td>{fmt.per(r.fixedPpm)}</td>
          </tr>
          <tr>
            <th scope="row">{copy.rowHome(v.fuel)}</th>
            <td>{fmt.per(r.homeCost)}</td>
          </tr>
          <tr>
            <th scope="row">{copy.rowAway(v.fuel)}</th>
            <td>{fmt.per(r.awayCost)}</td>
          </tr>
          <tr>
            <th scope="row">
              {copy.rowBlended(v.fuel)}
              <HelpTip label={help.blended.label}>{help.blended.body}</HelpTip>
            </th>
            <td>{fmt.per(r.energyPpm)}</td>
          </tr>
          <tr>
            <th scope="row">{copy.rowTotal}</th>
            <td>{fmt.rate(r.totalPpm)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}
